<template>
  <div
    v-if="localBlocks.length"
    class="promo-banner critical-component-hide"
    data-promo-banner-block
    aria-labelledby="promo-banner-title"
  >
    <span
      id="promo-banner-title"
      class="visually-hidden"
      v-text="$string('general.site_header.promo_banner')"
    />
    <div
      id="promo-banner-carousel"
      ref="carousel"
      class="splide"
      :aria-label="$string('accessibility.announcements')"
    >
      <div v-if="isABTestVar1 || useStatic" class="promo-banner__list desktop">
        <div class="splide__list">
          <div
            v-for="index of staticVisibleOrder"
            :key="localBlocks[index].key"
            class="promo-banner__list-item splide__slide"
          >
            <a
              v-if="localBlocks[index].url"
              class="promo-banner__list-item-link text-mobile-paragraph-s-regular"
              :href="localBlocks[index].url"
              >{{ localBlocks[index].text }}</a
            >

            <span
              v-else
              class="promo-banner__list-item-text text-mobile-paragraph-s-regular"
              v-text="localBlocks[index].text"
            />
            <div
              v-if="localBlocks[index].html"
              class="promo-banner__list-item-html"
              v-html="localBlocks[index].html"
            />
          </div>
        </div>
      </div>
      <div
        class="promo-banner__list splide__track"
        :class="{
          'is-static': isABTestVar1 || useStatic,
          'has-arrows': isABTestVar2 || showArrows,
        }"
        :options="carouselOptions"
      >
        <div class="splide__list">
          <div
            v-for="block of filteredBlocks"
            :key="block.key"
            class="promo-banner__list-item splide__slide"
          >
            <div
              v-if="block.html"
              class="promo-banner__list-item-html"
              v-html="block.html"
            />
            <a
              v-if="block.url"
              class="promo-banner__list-item-link text-mobile-paragraph-s-regular"
              :href="block.url"
              v-text="block.text"
            />

            <span
              v-else
              class="promo-banner__list-item-text text-mobile-paragraph-s-regular"
              v-text="block.text"
            />
          </div>
        </div>
      </div>

      <div v-if="showArrows || isABTestVar2" class="splide__arrows">
        <button class="splide__arrow splide__arrow--prev" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-chevron-left />
        </button>

        <button class="splide__arrow splide__arrow--next" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-chevron-right />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-env es2020, browser */
/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - Conditionally loaded when section has blocks using Liquid.
 *
 * @param {Array} blocks - Promo blocks.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} timing - Timing section settings.
 *
 */
import { Splide } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import { convertToHandle } from '~/helpers/convert';
import { dateTime } from '~/helpers/general';

import IconChevronLeft from '~icons/directional-navigation/chevron/left.svg';
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg';

// Module-scoped guard to avoid adding ad-hoc globals on window
let isPromoBannerSectionLoadBound = false;
// Trustpilot widget script (hoisted so it's not re-declared per instance)
const promoBannerScriptSrc =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

export default {
  name: 'PromoBanner',

  components: {
    IconChevronLeft,
    IconChevronRight,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    timing: {
      type: Number,
      default: 3000,
    },
    speed: {
      type: Number,
      default: 0.7,
    },
    showArrows: {
      type: Boolean,
      default: false,
    },
    // 0-based array index
    staticSlideOrder: {
      type: Array,
      default: () => [1, 0, 2],
    },
    useAutoScroll: {
      type: Boolean,
      default: false,
    },
    useStatic: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional: Only display HTML slides (promo-banner__list-item-html)
     * when a key exists in localStorage.
     * DEPRECATED: Originally used a generic flag (e.g. html-slides-enabled). Now superseded
     * by experiment key "kameleoonExperiment-344560" containing variant id "1199382".
     */
    htmlSlidesLocalStorageKey: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeSlide: 1,
      carousel: null,
      carouselOptions: {
        arrows: this.showArrows,
        autoHeight: true,
        autoplay: true,
        interval: this.timing * 1000,
        pagination: false,
        pauseOnFocus: false,
        pauseOnHover: true,
        rewind: true,
        type: this.showArrows ? 'loop' : 'fade',
        keyboard: true,
      },
      localBlocks: [],
      // Whether HTML slides (block.html) are allowed to render
      showHtmlSlides: false,
    };
  },

  computed: {
    isABTest() {
      const kameleeonInfos =
        globalThis.Kameleoon?.API?.Experiments?.getById(332907);
      const localStorageInfo = localStorage?.getItem(
        'kameleoonExperiment-332907',
      );
      return Boolean(
        /1170380|1170483/.test(localStorageInfo) ||
          (kameleeonInfos?.associatedVariation.id !== 0 &&
            kameleeonInfos?.active),
      );
    },

    /**
     * Mobile - auto scroll banderole
     * Desktop: show all slides statically next to each other at once
     */
    isABTestVar1() {
      const kameleeonInfos =
        globalThis.Kameleoon?.API?.Experiments?.getById(332907);
      return Boolean(
        localStorage
          ?.getItem('kameleoonExperiment-332907')
          ?.includes('1170380') ||
          (kameleeonInfos?.associatedVariation.id === 1170380 &&
            kameleeonInfos?.active),
      );
    },

    /**
     * Mobile/Desktop - slides with arrows, auto change
     */
    isABTestVar2() {
      const kameleeonInfos =
        globalThis.Kameleoon?.API?.Experiments?.getById(332907);
      return Boolean(
        localStorage
          ?.getItem('kameleoonExperiment-332907')
          ?.includes('1170483') ||
          (kameleeonInfos?.associatedVariation.id === 1170483 &&
            kameleeonInfos?.active),
      );
    },

    /**
     * Filter all blocks that shouldn't be shown normally (only inside A/B test)
     */
    filteredBlocks() {
      return this.localBlocks.filter((block) => {
        // Remove HTML blocks completely while toggle is off
        if (block.html && !this.showHtmlSlides) return false;
        return !block.hideOutsideTest || this.isABTest;
      });
    },

    autoScrollOptions() {
      const autoScroll = {
        speed: this.speed,
        autoStart: true,
        pauseOnHover: true,
      };

      return {
        autoScroll,
        arrows: false,
        type: 'loop',
        pagination: false,
        autoWidth: true,
        drag: true,
        gap: 'var(--spacing-3xl)',
      };
    },

    // For the static (desktop) variant using indices
    staticVisibleOrder() {
      // Apply same filtering rules as filteredBlocks
      return this.staticSlideOrder.filter((idx) => {
        const b = this.localBlocks[idx];
        if (!b) return false;
        if (b.html && !this.showHtmlSlides) return false;
        return !b.hideOutsideTest || this.isABTest;
      });
    },
  },

  watch: {
    async showHtmlSlides(newVal, oldVal) {
      if (newVal === oldVal) return;
      // If the slider exists, refresh after the DOM change
      try {
        await this.$nextTick();
        if (this.carousel) {
          this.carousel.refresh();
        }
      } catch (error) {
        // Attempt recovery by fully rebuilding the carousel
        if (this.carousel) {
          // Let any destroy errors surface instead of silently swallowing them
          this.carousel.destroy(true);
          this.carousel = null;
          await this.$nextTick();
          this.constructCarousel();
        }
        // If recovery fails again later, expose original error for visibility.
        // (Re-throw only if carousel is still null meaning construction failed)
        if (!this.carousel) {
          throw error;
        }
      }
      const trustpilotWidgets = this.$el.querySelectorAll(
        '[data-trustpilot-widget]',
      );
      if (
        trustpilotWidgets.length &&
        globalThis.Trustpilot &&
        typeof globalThis.Trustpilot.loadFromElement === 'function'
      ) {
        for (const el of trustpilotWidgets) {
          globalThis.Trustpilot.loadFromElement(el);
        }
      }
    },
  },

  created() {
    if (typeof globalThis === 'undefined') return;
    // created: localStorage verfügbar -> erste Auswertung
    this.updateShowHtmlSlides();
  },

  async mounted() {
    this.localBlocks = this.getLocalBlocks();
    this.carouselOptions.arrows = this.showArrows || this.isABTestVar2;
    this.carouselOptions.type =
      this.showArrows || this.isABTestVar2 ? 'loop' : 'fade';

    this.updateShowHtmlSlides();

    // Storage listener: respond only when the experiment key changes (multi-tab support)
    this.htmlSlidesStorageHandler = (ev) => {
      if (!ev || ev.key === 'kameleoonExperiment-344560') {
        this.updateShowHtmlSlides();
      }
    };
    globalThis.addEventListener('storage', this.htmlSlidesStorageHandler);

    setInterval(() => {
      this.updateBlocks();
    }, 1000);

    await this.$nextTick();

    this.constructCarousel();

    cnvs.EventBus.emit('cnvs:update-site-header-height');
    // Trustpilot Script Handling (moved to methods for reuse & clarity)
    this.loadPromoBannerScriptIfNeeded();
    this.bindPromoBannerSectionLoadListener();
  },

  beforeUnmount() {
    if (this.htmlSlidesStorageHandler) {
      globalThis.removeEventListener('storage', this.htmlSlidesStorageHandler);
    }
  },

  methods: {
    /**
     * Update blocks.
     */
    updateBlocks() {
      const newBlocks = this.getLocalBlocks();

      if (newBlocks.length === this.localBlocks.length) {
        return;
      }

      this.localBlocks = newBlocks;
    },

    /**
     * Construct and mount the carousel.
     */
    constructCarousel() {
      if (!this.$refs.carousel) {
        return;
      }

      this.carousel = new Splide(
        this.$refs.carousel,
        this.isABTestVar1 || this.useAutoScroll
          ? this.autoScrollOptions
          : this.carouselOptions,
      );

      if (this.isABTestVar1 || this.useAutoScroll) {
        this.carousel.mount({ AutoScroll });
      } else {
        this.carousel.mount();
      }
    },

    /**
     * Get local blocks by adding additional details to blocks prop.
     * @returns {Array}
     */
    getLocalBlocks() {
      return this.blocks
        .map((block, index) => {
          if (
            (!block.text && !block.html) ||
            (block.type === 'campaign' && !dateTime(block.start, block.end))
          ) {
            return false;
          }

          return {
            ...block,
            key: `promo-${index}-${convertToHandle(block.text)}`,
          };
        })
        .filter(Boolean);
    },
    /**
     * Evaluates whether HTML slides should be displayed based on localStorage
     * or Kameleoon API.
     */
    updateShowHtmlSlides() {
      // New gating logic (PIC-1433 update):
      // Instead of checking a generic html-slides-enabled key, we now rely on
      // the Kameleoon experiment key "kameleoonExperiment-344560" containing the variant id "1199382".
      // If that variant id is present => showHtmlSlides = true, else false.
      let isExperiment = false;
      const experimentValue =
        (typeof localStorage !== 'undefined' &&
          localStorage.getItem('kameleoonExperiment-344560')) ||
        '';

      isExperiment = experimentValue.includes('1199382');

      if (!experimentValue) {
        const experiment =
          globalThis.Kameleoon?.API?.Experiments?.getById(344560);
        isExperiment =
          experiment?.associatedVariation?.id === 1199382 && experiment?.active;
      }

      this.showHtmlSlides = isExperiment;
      // Legacy props kept for backward compatibility. Access (read) them in a no-op way
      // so they are not considered unused if provided externally. Intentionally not removed yet.
      // eslint-disable-next-line no-unused-vars
      const { htmlSlidesLocalStorageKey: _deprecatedKey } = this;
    },
    /**
     * Load the Trustpilot widget script only once.
     */
    loadPromoBannerScriptIfNeeded() {
      if (!promoBannerScriptSrc) return;
      // Only proceed if this component root has the marker attribute
      if (!(this.$el && this.$el.matches('[data-promo-banner-block]'))) return;

      if (
        document.querySelector(`script[data-dynamic="${promoBannerScriptSrc}"]`)
      )
        return;
      const externalScript = document.createElement('script');
      externalScript.src = promoBannerScriptSrc;
      externalScript.defer = true;
      externalScript.dataset.dynamic = promoBannerScriptSrc;
      document.head.appendChild(externalScript);
    },
    /**
     * Bind a single global listener that loads the script whenever a Shopify section
     * containing a promo banner block is dynamically loaded.
     */
    bindPromoBannerSectionLoadListener() {
      if (isPromoBannerSectionLoadBound) return;
      isPromoBannerSectionLoadBound = true;
      document.addEventListener('shopify:section:load', (event) => {
        if (
          event.target &&
          event.target.querySelector('[data-promo-banner-block]')
        ) {
          this.loadPromoBannerScriptIfNeeded();
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import './promo-banner';
@import '@/components/splide';
</style>
